import React, { Suspense } from 'react';
import { useSelector } from 'react-redux';
import { ScrollingProvider, TealiumScriptLoadedProvider } from 'modules/core';
import { NotFoundPage, PageLoader } from 'modules/ui';
import { AccountSwitch, ContentCreatorApprovedPage } from 'modules/accounts';
import { NotificationProvider } from 'modules/notifications';
import {
  LoginPage,
  ChooseSignUpPage,
  SignUpPage,
  WelcomePage,
  VerificationEmailPage,
  ForgotPasswordPage,
  SuccessPage,
  ResetPasswordPage,
  CompleteNewPasswordPage,
  FarewellPage,
  AuthRoute,
  getIsAuthenticated,
  ProtectedRoute,
  RedirectionProvider,
} from 'modules/auth';
import './fonts';
import TopCandidatesSharingPage from './modules/sharing/fragments/TopCandidatesSharingPage';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import {
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider,
  Route,
  Outlet,
} from 'react-router-dom';

export default function App() {
  const authenticated = useSelector(getIsAuthenticated);
  const theme = createTheme();
  const router = createBrowserRouter(
    createRoutesFromElements(
      <Route
        element={
          <>
            <RedirectionProvider />
            <Outlet />
          </>
        }
      >
        <Route
          path="/login"
          element={
            <AuthRoute>
              <LoginPage />
            </AuthRoute>
          }
        />
        <Route
          path="/signup"
          element={
            <AuthRoute>
              <ChooseSignUpPage />
            </AuthRoute>
          }
        />
        <Route
          exact
          path="/signup/:accountType/:firstName/:lastName/:email"
          element={
            <AuthRoute>
              <SignUpPage />
            </AuthRoute>
          }
        />
        <Route
          exact
          path="/signup/:accountType"
          element={
            <AuthRoute>
              <SignUpPage />
            </AuthRoute>
          }
        />
        <Route
          path="/welcome"
          element={
            <AuthRoute>
              <WelcomePage />
            </AuthRoute>
          }
        />
        <Route
          path="/success"
          element={
            <AuthRoute>
              <SuccessPage />
            </AuthRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <AuthRoute>
              <ForgotPasswordPage />
            </AuthRoute>
          }
        />
        <Route
          path="/auth/verify-email"
          element={
            <AuthRoute>
              <VerificationEmailPage />
            </AuthRoute>
          }
        />
        <Route
          path="/auth/reset-password"
          element={
            <AuthRoute>
              <ResetPasswordPage />
            </AuthRoute>
          }
        />
        <Route
          path="/auth/complete-new-password"
          element={
            <AuthRoute>
              <CompleteNewPasswordPage />
            </AuthRoute>
          }
        />
        <Route
          exact
          path="/accounts/content-creator-approved/:name/:lastname/:email/:token"
          element={<ContentCreatorApprovedPage />}
        />
        <Route
          exact
          path="/shared/top-candidates/:token"
          element={<TopCandidatesSharingPage />}
        />
        <Route exact path="/farewell" element={<FarewellPage />} />
        {authenticated && <Route path="*" element={<AccountSwitch />} />}
        <Route
          exact
          path="*"
          element={
            <ProtectedRoute>
              <NotFoundPage />
            </ProtectedRoute>
          }
        />
      </Route>,
    ),
  );

  return (
    <React.StrictMode>
      <NotificationProvider />
      <TealiumScriptLoadedProvider />
      <RouterProvider router={router}>
        <ScrollingProvider />
        <Suspense fallback={<PageLoader />}>
          <ThemeProvider theme={theme}></ThemeProvider>
        </Suspense>
      </RouterProvider>
    </React.StrictMode>
  );
}
